import React, { useCallback, useState, useEffect, useRef } from "react"
import Form from "@bappweb/form"
import Panel from "@bappweb/panel"
import TextField from "@bappweb/textfield"
import Button from "@bappweb/button"
import Captcha from "@smpu-web/nested/form/captcha"
import FormMessage from "@smpu-web/globalmodules/form-message/index"
import Page from "@smpu-web/globalmodules/page"
import Element from "@bappweb/element"
import RadioGroup from "@bappweb/radiogroup"
import Radio from "@bappweb/radio"
import { required, minLength, RegexpTypes, getRegex, numeric } from "@bappweb/utils"
import { useGetTokenByTckn1Mutation, useGetCaptchaMutation, useRefreshCaptchaMutation, useApplyMutation } from "../../api/services"
import "./assets/style.scss"
import "src/assets/styles/style.scss"
import "../../assets/style.scss"

type PropsType = {
  onError?: (event?: React.BaseSyntheticEvent, errorField?: Record<string, any>) => void
  onSubmit?: (event?: React.BaseSyntheticEvent, values?: Record<string, any>) => void
  onItemsFetched?: any,
  reportYear: number
}

function MainForm({ onError, onSubmit, onItemsFetched, reportYear }: PropsType) {

  const refForm = useRef(null)

  const [identityNumber, setIdentityNumber] = useState('')
  const [vknNumber, setVknNumber] = useState("")
  const [accountOwnerType, setAccountOwnerType] = useState('K')

  const [captchaValues, setCaptchaValues] = useState({
    tokenSessionId: "",
    captchaData: "",
    captcha: "",
    showCaptcha: false,
    captchaValidate: true
  })

  const [error, setError] = useState<string>()
  const [isFetching, setIsFetching] = useState(false)

  const [getCaptcha] = useGetCaptchaMutation();
  const [getTokenByTckn1] = useGetTokenByTckn1Mutation();

  const createCaptchaPayload = useCallback(() => {
    return accountOwnerType === 'K'
      ? {
        TokenSessionId: captchaValues.tokenSessionId,
        Language: 'tr-TR',
        tckn: identityNumber
      }
      : {
        TokenSessionId: captchaValues.tokenSessionId,
        Language: 'tr-TR',
        vkn: vknNumber
      };
  }, [accountOwnerType, captchaValues.tokenSessionId, identityNumber, vknNumber]);

  useEffect(() => {
    if (captchaValues.captchaData === "" && captchaValues.tokenSessionId !== "") {
      setIsFetching(true)
      const data = createCaptchaPayload();
      getCaptcha(data)
        .unwrap()
        .then((payload) => {
          if (!payload.IsError) {
            setError('')
            setCaptchaValues({
              ...captchaValues,
              showCaptcha: true,
              captchaData: payload.captchaData,
              captchaValidate: true
            })
          }
          else {
            setError(payload.ErrorMessage ? payload.ErrorMessage : "")
          }
        })
        .finally(() => { setIsFetching(false) })
    }
  }, [captchaValues, getCaptcha, identityNumber, vknNumber, accountOwnerType])

  const clearCaptcha = () => {
    setCaptchaValues({
      ...captchaValues,
      tokenSessionId: "",
      captchaData: "",
      showCaptcha: false
    })

    // @ts-ignore TODO
    refForm.current?.setValue("captcha", null)
  }

  const handleChange = useCallback((e) => {
    const formName = e.target.name
    const formValue = e.target.value

    switch (formName) {
      case "identityNumber":
        setIdentityNumber(formValue)
        clearCaptcha()
        onItemsFetched([])
        if (formValue.length === 11) {
          setIsFetching(true)
          getTokenByTckn1(formValue)
            .unwrap()
            .then((payload) => {
              if (!payload.IsError) {
                setError('')
                setCaptchaValues({
                  ...captchaValues,
                  tokenSessionId: payload.tokenSessionId,
                })
              } else {
                setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              }
            })
            .finally(() => { setIsFetching(false) })
        }
        break;
      case "vknNumber":
        setVknNumber(formValue)
        clearCaptcha()
        onItemsFetched([])
        if (formValue.length === 10) {
          setIsFetching(true)
          getTokenByTckn1(formValue)
            .unwrap()
            .then((payload) => {
              if (!payload.IsError) {
                setError('')
                setCaptchaValues({
                  ...captchaValues,
                  tokenSessionId: payload.tokenSessionId,
                })
              } else {
                setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              }
            })
            .finally(() => { setIsFetching(false) })
        }
        break;
      case "accountOwnerType":
        setAccountOwnerType(formValue)
        if (formValue === "K") {
          setVknNumber('')
          // @ts-ignore TODO
          refForm.current?.setValue("vknNumber", null)
        } else if (formValue === "T") {
          setIdentityNumber('')
          // @ts-ignore TODO
          refForm.current?.setValue("identityNumber", null)
        }
        clearCaptcha()
        onItemsFetched([])
        break;
      case "captcha":
        setCaptchaValues({
          ...captchaValues,
          captcha: formValue,
          captchaValidate: true
        })
        break;
      default:
        break;
    }
  }, [getTokenByTckn1, captchaValues])

  const [refreshCaptcha] = useRefreshCaptchaMutation();
  const handleCaptchaRefresh = useCallback(() => {
    setIsFetching(true)
    const data = createCaptchaPayload();

    refreshCaptcha(data)
      .unwrap()
      .then((payload) => {
        if (!payload.IsError) {
          setError('')
          setCaptchaValues({
            ...captchaValues,
            captchaData: payload.captchaData,
            captchaValidate: true,
            captcha: "",
            showCaptcha: true,
          })
        } else {
          setError(payload.ErrorMessage ? payload.ErrorMessage : "")
        }
      })
      .finally(() => { setIsFetching(false) })
  }, [createCaptchaPayload, refreshCaptcha])

  const [apply] = useApplyMutation()

  const handleSubmit = () => {
    const captchaVal = captchaValues && captchaValues.captcha ? captchaValues.captcha.toUpperCase() : ''
    if (((identityNumber && identityNumber.length === 11) || (vknNumber && vknNumber.length === 10)) && captchaVal && captchaValues.tokenSessionId && accountOwnerType) {
      setIsFetching(true)
      apply({
        tckn: identityNumber,
        vkn: vknNumber,
        accountOwnerType,
        reportYear,
        captcha: captchaVal,
        TokenSessionId: captchaValues.tokenSessionId
      })
        .unwrap()
        .then((payload) => {
          if (!payload.IsError) {
            setError('')
            if (onItemsFetched) {
              onItemsFetched(payload.items)
              window.scrollTo({
                top: 1000,
                behavior: "smooth",
              });
            }
          } else {
            setError(payload.ErrorMessage ? payload.ErrorMessage : "")
          }
        })
        .finally(() => {
          const data = createCaptchaPayload();
          refreshCaptcha(data)
            .unwrap()
            .then((payload) => {
              if (!payload.IsError) {
                setCaptchaValues({
                  ...captchaValues,
                  captcha: "",
                  showCaptcha: true,
                  captchaData: payload.captchaData,
                  captchaValidate: false
                })
              }
              else {
                setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              }
            })
            .finally(() => { setIsFetching(false) })
        })
    } else {
      setError("Lütfen girdiğiniz bilgileri kontrol edin.")
    }
  }
  return (
    <Page>
      <Form onSubmit={handleSubmit} onChange={handleChange} grid={false} ref={refForm}>
        <Panel hasFooter connected={false} loading={isFetching}>
          <FormMessage className={"form-msg-error"} error={error}></FormMessage>
          <Panel.Body className="bapp-panel-body-transparent">
            <RadioGroup
              value={accountOwnerType}
              name="accountOwnerType"
              block={false}
              className="ytm-hesapsahibi-radio">
              <Radio label="Kişisel" name="accountOwnerType" value="K" />
              <Radio label="Tüzel" name="accountOwnerType" value="T" />
            </RadioGroup>
            {accountOwnerType === "K" &&
              <TextField
                label='TC Kimlik No'
                type="text"
                name="identityNumber"
                maxLength={11}
                value={identityNumber}
                validator={RegexpTypes.Numeric}
                validations={[required("TC Kimlik Numarası alanı zorunludur."), numeric(), minLength(11, "TCKN bilgisi 11 hane girilmelidir")]}
              />

            }
            {accountOwnerType === "T" &&
              <TextField
                type="text"
                name="vknNumber"
                label="Vergi Kimlik No"
                maxLength={10}
                value={vknNumber}
                validator={RegexpTypes.Numeric}
                validations={[required("Lütfen VKN Numaranızı giriniz"), numeric(), minLength(10, "VKN için 10 hane girilmelidir. 10 haneden küçük VKN numaraları için lütfen başına sıfır ekleyiniz.")]}
              />
            }
            {captchaValues.showCaptcha && (
              <Captcha
                label="Güvenlik Kodu"
                id="captcha"
                type="text"
                name="captcha"
                placeholder="Güvenlik Kodu"
                captcha={captchaValues.captchaData}
                value={captchaValues.captcha}
                maxLength={4}
                maxRefreshCount={3}
                captchaValidate={captchaValues.captchaValidate}
                refreshClick={handleCaptchaRefresh}
                validations={[required("Lütfen güvenlik kodunu giriniz."), minLength(4, "Güvenlik kodu 4 hane girilmelidir")]}
              />
            )}

          </Panel.Body>
          <Panel.Footer>
            <Panel.RightFooter>
              <Button
                text="Ara" type="submit" fetching={isFetching} disabled={isFetching}
                onClick={() =>
                  setCaptchaValues({
                    ...captchaValues,
                    captchaValidate: true
                  })
                } />
            </Panel.RightFooter>
          </Panel.Footer>
        </Panel>
      </Form>
    </Page>
  )
}

export default MainForm
